/**
 * Cloudflare Turnstile widget (explicit render). Loads api.js once; no extra CAPTCHA library.
 *
 * Sitekey comes from `VITE_TURNSTILE_SITE_KEY` with a public fallback to the existing widget.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type TurnstileAction = "login" | "magic-link" | "password-reset";

export interface TurnstileFieldHandle {
  reset: () => void;
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SITE_KEY_FALLBACK = "0x4AAAAAAES_DQIguTjJlclN";

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
    },
  ) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function siteKey(): string {
  const configured = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return typeof configured === "string" && configured.length > 0 ? configured : SITE_KEY_FALLBACK;
}

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const onReady = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile"));
    };
    if (existing) {
      existing.addEventListener("load", onReady);
      existing.addEventListener("error", () => reject(new Error("turnstile")));
      if (window.turnstile) onReady();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = onReady;
    script.onerror = () => reject(new Error("turnstile"));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export const TurnstileField = forwardRef<
  TurnstileFieldHandle,
  {
    action: TurnstileAction;
    onToken: (token: string | null) => void;
  }
>(function TurnstileField({ action, onToken }, ref) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const [ready, setReady] = useState(false);

  onTokenRef.current = onToken;

  const emit = useCallback((token: string | null) => {
    setReady(Boolean(token));
    onTokenRef.current(token);
  }, []);

  useImperativeHandle(ref, () => ({
    reset() {
      const id = widgetIdRef.current;
      if (id && window.turnstile) window.turnstile.reset(id);
      emit(null);
    },
  }));

  useEffect(() => {
    let cancelled = false;
    let renderedId: string | null = null;
    emit(null);

    void loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        renderedId = turnstile.render(containerRef.current, {
          sitekey: siteKey(),
          action,
          callback: (token) => {
            if (!cancelled) emit(token);
          },
          "expired-callback": () => {
            if (!cancelled) emit(null);
          },
          "error-callback": () => {
            if (!cancelled) emit(null);
          },
        });
        if (cancelled) {
          turnstile.remove(renderedId);
          renderedId = null;
          return;
        }
        widgetIdRef.current = renderedId;
      })
      .catch(() => {
        if (!cancelled) emit(null);
      });

    return () => {
      cancelled = true;
      widgetIdRef.current = null;
      if (renderedId && window.turnstile) window.turnstile.remove(renderedId);
    };
  }, [action, emit]);

  return (
    <div
      className="pt-1"
      data-testid="turnstile-widget"
      data-turnstile-action={action}
      data-turnstile-ready={ready ? "true" : "false"}
    >
      <p className="sr-only">{t("auth.turnstileLabel")}</p>
      <div ref={containerRef} />
    </div>
  );
});

TurnstileField.displayName = "TurnstileField";
